import { useState, useEffect, useRef } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { Button, Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { CombinedGraphQLErrors } from '@apollo/client'
import Spinner from '../Spinner/Spinner'
import LoginImage from './LoginImage'
import LoginMessage from './LoginMessage'
import SignUpLink from './SignUpLink.js'
import { signIn, deepCopy, getTenant, removeItem } from '../../utils'
import { LOGIN_MUTATION } from '../../graphql.js'
import MFA from './MFA'
import LoginTitle from './LoginTitle'
import LoginFooter from './LoginFooter'
import LoginError from './LoginError'
import './Login.css'
import ConfigValue from '../ConfigValue/ConfigValue'

const LOGIN = gql(LOGIN_MUTATION)

export default function Login() {
  console.log('Login rendered for tenant: ', getTenant())

  const [inputs, setInputs] = useState({ email: '', password: '' })
  const [emailValid, setEmailValid] = useState({ emailValid: null, emailFeedback: '' })
  const [passwordValid, setPasswordValid] = useState({ passwordValid: null, passwordFeedback: '' })
  const [redirect, setRedirect] = useState()
  const [loginError, setLoginError] = useState()
  const [loginMutation, { loading: mutationLoading, error: mutationError }] = useMutation(LOGIN)
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)
  const [mfaComponent, setMfaComponent] = useState(null)

  // focus on the email input after render
  const emailInput = useRef(null)
  useEffect(() => {
    emailInput.current.focus()
    const tenant = getTenant()
    removeItem(tenant)
    document.title = 'CE2 - Login'
  }, [])

  function handleInputChange(event) {
    const newInputs = deepCopy(inputs)
    newInputs[event.target.name] = event.target.value
    setInputs(newInputs)
    return true
  }

  async function handleLogin() {
    console.log('handleLogin: ', inputs)
    setLoginError(null)
    let valid = true
    if (!inputs.email) {
      setEmailValid({ emailValid: false, emailFeedback: 'Email is required' })
      valid = false
    } else {
      if (inputs.email.indexOf('@') === -1 || inputs.email.indexOf('.') === -1) {
        setEmailValid({ emailValid: false, emailFeedback: 'not a valid email address' })
        valid = false
      } else {
        setEmailValid({ emailValid: null, emailFeedback: '' })
        valid = true
      }
    }
    if (!inputs.password) {
      setPasswordValid({ passwordValid: false, passwordFeedback: 'Password is required' })
      valid = false
    } else {
      setPasswordValid({ passwordValid: null, passwordFeedback: '' })
      valid = true
    }
    if (valid) {
      await loginMutation({ variables: { email: inputs.email, password: inputs.password } })
        .then((res) => {
          console.log('Login.handleLogin:', res)
          if (res.data.login.mfa) {
            toggle()
            setMfaComponent(<MFA handleLogin={handleLogin} loginData={res.data.login} modal={true} toggle={toggle} setMfaComponent={setMfaComponent} />)
            return
          }
          signIn(res.data.login)
          setRedirect('/home')
        })
        .catch((error) => {
          if (CombinedGraphQLErrors.is(error)) {
            const gqlErrors = error.errors.map((gqlError) => {
              return gqlError.message
            })
            console.log(gqlErrors)
            setLoginError(gqlErrors[0])
            if (gqlErrors[0].indexOf('password') >= 0) {
              setPasswordValid({ passwordValid: false, passwordFeedback: gqlErrors[0] })
              setEmailValid({ emailValid: true, emailFeedback: null })
            }
            if (gqlErrors[0].indexOf('email') >= 0) {
              setEmailValid({ emailValid: false, emailFeedback: gqlErrors[0] })
              setPasswordValid({ passwordValid: null, passwordFeedback: null })
            }
          }
        })
    }
  }
  
  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      if (!modal) {
        handleLogin()
      }
    }
  }

  let buttonComponent = <Button color="primary" onClick={handleLogin} block data-cy="login-button" style={{ width: '100%', fontSize: '1.25rem'}}>Sign In</Button>
  if (mutationLoading) {
    buttonComponent = <Spinner />
  }

  if (redirect) return <Navigate to={redirect} />

  return (
    <div className="max-width-panel" >
      <div className="panel panel-default bootstrap-basic">
        {mfaComponent}
        <div className="panel-heading">
          <div className="text-center">
            <LoginImage />
            <LoginTitle />
            <div style={{ marginBottom: '2px' }}><ConfigValue configKey={'CLIENT VERSION'} /></div>
            <LoginMessage />
            <LoginError loginError={loginError} mutationError={mutationError} />
          </div>
        </div>
        <Form className="panel-body">
          <FormGroup>
            <Label for="login-email">Email</Label>
            <Input
              innerRef={emailInput}
              type="text"
              name="email"
              value={inputs.email}
              invalid={emailValid.emailValid === false}
              valid={emailValid.emailValid === true}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              data-cy="login-email"
              autoComplete="username"
            />
            <FormFeedback>
              {emailValid.emailFeedback}
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="login-password">Password</Label>
            <Input
              type="password"
              name="password"
              value={inputs.password}
              invalid={passwordValid.passwordValid === false}
              valid={passwordValid.passwordValid === true}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              data-cy="login-password"
              autoComplete="current-password"
            />
            <FormFeedback>
              {passwordValid.passwordFeedback}
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Link to="/forgotPassword" data-cy="login-forgot-password">Forgot password?</Link>
          </FormGroup>
          {buttonComponent}
        </Form>
        <SignUpLink />
        <LoginFooter />
      </div>
    </div>
  )
}